// §14 Application submitted — reference + Customer ID from the submit
// response, copy buttons, what-happens-next, Continue → /applicant.
// If the in-memory result is gone (e.g. refresh), rebuild it from the backend
// using the tracking session's reference.
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { PRODUCTS } from '@/api/constants';
import { useOnboarding } from '@/context/OnboardingContext';
import { getSession } from '@/api/session';
import { getApplicationByReference } from '@/api/onboarding';
import type { SubmitResult, ProductType } from '@/types/onboarding.types';
import './onboarding.css';

const Submitted = () => {
  const navigate = useNavigate();
  const { draft, resetDraft } = useOnboarding();
  const [copied, setCopied] = useState('');
  const [fallback, setFallback] = useState<SubmitResult | null>(null);

  const result = draft.submitted ?? fallback;

  useEffect(() => {
    if (draft.submitted) return;
    const session = getSession();
    if (!session?.reference) return;
    let cancelled = false;
    getApplicationByReference(session.reference, session.mobileNumber)
      .then((app) => {
        if (cancelled) return;
        setFallback({
          reference: app.reference,
          customerId: app.customerId,
          product: app.product as ProductType,
          mobileMasked: app.maskedMobile,
          firstName: app.firstName,
          submittedAt: app.submittedAt ?? '',
          handover: '',
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [draft.submitted]);

  if (!result) {
    return <Navigate to="/open-account" replace />;
  }

  const productName = PRODUCTS.find((p) => p.type === result.product)?.name ?? result.product;

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(`${label} copied`);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  const handleContinue = () => {
    resetDraft(); // onboarding draft is cleared
    navigate('/applicant', { replace: true });
  };

  return (
    <div className="nova-ob-success-screen">
      <div className="nova-ob-tick" aria-hidden="true">✓</div>
      <h1 className="nova-ob-heading">Application submitted</h1>
      <p>
        Thank you, {result.firstName}. Your {productName} application is with the bank.
      </p>

      <div className="nova-ob-card w-100" style={{ maxWidth: 480 }}>
        <div className="nova-ob-copyrow">
          <span className="text-muted">Application reference</span>
          <span className="fw-semibold">
            {result.reference}{' '}
            <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={() => copy('Application reference', result.reference)}>
              Copy
            </button>
          </span>
        </div>
        <div className="nova-ob-copyrow">
          <span className="text-muted">Customer ID</span>
          <span className="fw-semibold">
            {result.customerId}{' '}
            <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={() => copy('Customer ID', result.customerId)}>
              Copy
            </button>
          </span>
        </div>
        <div className="nova-ob-copyrow">
          <span className="text-muted">Account type</span>
          <span className="fw-semibold">{productName}</span>
        </div>
        <div className="nova-ob-copyrow" style={{ borderBottom: 'none' }}>
          <span className="text-muted">Registered mobile</span>
          <span className="fw-semibold">{result.mobileMasked}</span>
        </div>
      </div>

      {copied && <div className="text-success mb-2">{copied}</div>}

      <div className="nova-ob-info w-100" style={{ maxWidth: 480 }}>
        The bank now verifies your documents and activates the account. You will get an SMS and
        an email when it is ready, usually within one working day.
      </div>

      <div className="w-100 text-start" style={{ maxWidth: 480 }}>
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>What happens next</h2>
        <ol className="ps-3">
          <li className="mb-2">Track your application on the home screen. Later you can log in again with your mobile number.</li>
          <li className="mb-2">Once active, set up net banking, UPI and your debit card from the home screen.</li>
          <li className="mb-2">Complete Video KYC from the home screen — your account is activated after it.</li>
          <li className="mb-2">Look out for the welcome kit at your address.</li>
        </ol>
      </div>

      <div className="nova-ob-footer">
        <div className="nova-ob-footer-inner">
          <button type="button" className="btn btn-primary btn-lg w-100 fw-semibold nova-btn" onClick={handleContinue}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default Submitted;
